import { Select } from "@/components/ui/Select";
import { FormControl } from "@/components/ui/FormControl";
import classes from "./PersonalDetails.module.scss";
import { Input } from "@/components/ui/Input";
import type { PersonalDetailsProps } from "./PersonalDetailsProps";
import { useTranslation } from "@/hooks/useTranslation";
import { ValidationGroupProvider } from "@/contexts/ValidationGroupProvider";
import { Field } from "../Field";
import { VerificationWarning } from "../VerificationWarning";
import { useEmailVerification } from "@/hooks/useEmailVerification";
import { useImperativeHandle, useRef } from "react";
import type { ImperativeHandle } from "@/types/Form";
import { getCurrentStateValues } from "@/stores";
import * as z from "zod";
import { alphaNameSchema } from "@/utils/validators";
import type { PersonalDetails as PersonalDetailsType } from "@/stores";

const isEmailShaped = (value: string) =>
  z.string().email().safeParse(value).success;

export function PersonalDetails(props: PersonalDetailsProps) {
  const { config, className, ref } = props;

  const t = useTranslation("personal_details");

  const {
    issue: emailIssue,
    checkEmail,
    clearIssue: clearEmailIssue,
  } = useEmailVerification();

  const groupRef = useRef<ImperativeHandle>(null);
  // The Loqate email check runs as a gate when leaving the step (on Continue),
  // not on blur — so it can't race the Continue click. Once an email has been
  // checked (clean) or the donor chose to keep it, it is recorded here so
  // re-clicking Continue advances instead of re-warning on the same value.
  const acknowledgedEmailRef = useRef<string | null>(null);

  useImperativeHandle(ref, () => ({
    validate: async () => {
      const fieldResult = (await groupRef.current?.validate()) ?? {
        isValid: true,
      };
      if (!fieldResult.isValid) {
        return fieldResult;
      }

      const currentEmail = getCurrentStateValues().personalDetails.email;
      if (
        !isEmailShaped(currentEmail) ||
        currentEmail === acknowledgedEmailRef.current
      ) {
        return { isValid: true };
      }

      const raisedWarning = await checkEmail(currentEmail);
      if (raisedWarning) {
        return { isValid: false };
      }

      acknowledgedEmailRef.current = currentEmail;
      return { isValid: true };
    },
  }));

  const titleOptions = config["personal-details-title-options"].map(
    (option) => ({
      value: option.value,
      label: t(option.i18n),
    }),
  );

  const {
    title,
    "first-name": firstName,
    "last-name": lastName,
    email,
    "confirm-email": confirmEmail,
  } = config["personal-details-fields"];

  return (
    <ValidationGroupProvider name="personalDetails" ref={groupRef}>
      <div className={className}>
        <div className={classes.formGrid}>
          <Field<PersonalDetailsType>
            name="title"
            serverErrorPaths={["personal_details.title"]}
            validate={() => {
              if (title["is-required"]) {
                return z.string().min(1, t("errors.title-required") as string);
              }
              return null;
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(title.label.i18n)}
                error={errorMessage}
                isValid={isValid}
                required={title["is-required"]}
              >
                <Select
                  ref={ref as React.RefObject<HTMLSelectElement>}
                  name="title"
                  value={value}
                  onChange={(ev) => handleChange(ev.target.value)}
                  onBlur={() => handleBlur()}
                >
                  <option value="">{t("title-placeholder")}</option>
                  {titleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Field<PersonalDetailsType>
            name="firstName"
            serverErrorPaths={["personal_details.first_name"]}
            validate={() => {
              if (firstName["is-required"]) {
                return alphaNameSchema({
                  requiredMessage: t("errors.first-name-required") as string,
                  invalidMessage: t("errors.first-name-invalid") as string,
                });
              }
              return alphaNameSchema({
                invalidMessage: t("errors.first-name-invalid") as string,
              });
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(firstName.label.i18n)}
                error={errorMessage}
                isValid={isValid}
                required={firstName["is-required"]}
              >
                <Input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  name="firstName"
                  value={value}
                  onChange={(e) => handleChange(e.target.value, e)}
                  onBlur={() => handleBlur()}
                />
              </FormControl>
            )}
          />

          <Field<PersonalDetailsType>
            name="lastName"
            serverErrorPaths={["personal_details.last_name"]}
            validate={() => {
              if (lastName["is-required"]) {
                return alphaNameSchema({
                  requiredMessage: t("errors.last-name-required") as string,
                  invalidMessage: t("errors.last-name-invalid") as string,
                });
              }
              return alphaNameSchema({
                invalidMessage: t("errors.last-name-invalid") as string,
              });
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <FormControl
                label={t(lastName.label.i18n)}
                error={errorMessage}
                isValid={isValid}
                required={lastName["is-required"]}
              >
                <Input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  name="lastName"
                  value={value}
                  onChange={(e) => {
                    handleChange(e.target.value, e);
                  }}
                  onBlur={() => handleBlur()}
                />
              </FormControl>
            )}
          />

          <Field<PersonalDetailsType>
            name="email"
            serverErrorPaths={["personal_details.email"]}
            validate={() => {
              if (email["is-required"]) {
                return z.string().email(t("errors.email-invalid") as string);
              }
              return z
                .string()
                .email(t("errors.email-invalid") as string)
                .optional();
            }}
            render={({
              value,
              handleChange,
              handleBlur,
              ref,
              errorMessage,
              isValid,
            }) => (
              <div>
                <FormControl
                  label={t(email.label.i18n)}
                  error={errorMessage}
                  isValid={isValid}
                  required={email["is-required"]}
                >
                  <Input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    type="email"
                    name="email"
                    value={value}
                    onChange={(e) => {
                      handleChange(e.target.value, e);
                      clearEmailIssue();
                      // A new value must be re-verified on the next Continue.
                      acknowledgedEmailRef.current = null;
                    }}
                    onBlur={() => handleBlur()}
                  />
                </FormControl>
                {emailIssue && (
                  <VerificationWarning
                    message={
                      (emailIssue.suggestion
                        ? t("warnings.email-did-you-mean", {
                            email: emailIssue.suggestion,
                          })
                        : t("warnings.email-unverified")) as string
                    }
                    suggestion={
                      emailIssue.suggestion
                        ? {
                            label: t("warnings.email-use-suggestion", {
                              email: emailIssue.suggestion,
                            }) as string,
                            onAccept: () => {
                              const suggested = emailIssue.suggestion as string;
                              handleChange(suggested);
                              // Accepted correction is trusted; don't re-warn it.
                              acknowledgedEmailRef.current = suggested;
                              clearEmailIssue();
                            },
                          }
                        : null
                    }
                    onDismiss={() => {
                      // "Keep mine" = proceed; remember so Continue advances.
                      acknowledgedEmailRef.current = value;
                      clearEmailIssue();
                    }}
                    dismissLabel={t("warnings.email-keep-mine") as string}
                  />
                )}
              </div>
            )}
          />

          {confirmEmail.enabled !== false && (
            <Field<PersonalDetailsType>
              name="confirmEmail"
              dependencies={["email"]}
              serverErrorPaths={["personal_details.confirm_email"]}
              validate={(groupStateValues) => {
                if (email["is-required"]) {
                  return z
                    .string()
                    .email(t("errors.email-invalid") as string)
                    .refine(
                      (val) => val === groupStateValues.email,
                      t("errors.email-mismatch") as string,
                    );
                }

                return z
                  .string()
                  .email(t("errors.email-invalid") as string)
                  .refine(
                    (val) => val === groupStateValues.email,
                    t("errors.email-mismatch") as string,
                  )
                  .optional();
              }}
              render={({
                value,
                handleChange,
                handleBlur,
                ref,
                errorMessage,
                isValid,
              }) => (
                <FormControl
                  label={t(confirmEmail.label.i18n)}
                  error={errorMessage}
                  isValid={isValid}
                  required={email["is-required"]}
                >
                  <Input
                    ref={ref as React.RefObject<HTMLInputElement>}
                    type="email"
                    name="confirmEmail"
                    value={value}
                    onChange={(e) => handleChange(e.target.value, e)}
                    onBlur={() => handleBlur()}
                  />
                </FormControl>
              )}
            />
          )}
        </div>
      </div>
    </ValidationGroupProvider>
  );
}
